import "../styles/diagram.css";
import { useInView } from "react-intersection-observer";
import { useState } from "react";

// Rich content for each intelligence item — ES + EN
const ITEM_DETAILS = {
  // ── Operational intelligence ──────────────────────────────────────────────
  "Inteligencia operacional": {
    icon: "⚙️",
    description:
      "Monitorea en tiempo real el desempeño operacional de tus SKUs en cada portal: disponibilidad, posición y estado de inventario actualizados diariamente en más de 25 cadenas de LATAM.",
    keywords: ["disponibilidad de producto", "inventario e-commerce", "monitoreo de SKUs"],
  },
  "Operational intelligence": {
    icon: "⚙️",
    description:
      "Monitor in real time the operational performance of your SKUs across every portal: availability, position and inventory status updated daily across 25+ LATAM retailers.",
    keywords: ["product availability", "e-commerce inventory", "SKU monitoring"],
  },

  // ── Market intelligence ───────────────────────────────────────────────────
  "Inteligencia de mercado": {
    icon: "📊",
    description:
      "Entiende la dinámica completa del mercado digital: participación de marcas, tendencias por categoría, comportamiento de la competencia y evolución semanal de precios y promociones.",
    keywords: ["analítica de mercado", "digital shelf analytics", "inteligencia competitiva"],
  },
  "Market intelligence": {
    icon: "📊",
    description:
      "Understand full digital market dynamics: brand share, category trends, competitive behavior, and weekly evolution of prices and promotions.",
    keywords: ["market analytics", "digital shelf analytics", "competitive intelligence"],
  },

  // ── Price Matching ────────────────────────────────────────────────────────
  "Comparación de precios": {
    icon: "🔍",
    description:
      "Compara los precios de tu marca y los de la competencia en múltiples retailers simultáneamente. Detecta diferencias, identifica quién tiene el mejor precio y actúa antes que los demás.",
    keywords: ["comparación de precios online", "price matching retail", "monitoreo de precios"],
  },
  "Price Matching": {
    icon: "🔍",
    description:
      "Compare your brand's prices and your competitors' across multiple retailers simultaneously. Spot gaps, identify who has the best price, and act faster than the market.",
    keywords: ["online price comparison", "retail price matching", "price monitoring"],
  },

  // ── eCommerce Price Competition ───────────────────────────────────────────
  "Competencia de precios en eCommerce": {
    icon: "⚡",
    description:
      "Analiza la guerra de precios en e-commerce en tiempo real. Detecta quién recorta precios, con qué frecuencia, qué profundidad de descuento usan y cómo impacta tu posicionamiento.",
    keywords: ["competencia de precios", "e-commerce pricing", "estrategia de precios"],
  },
  "eCommerce Price Competition": {
    icon: "⚡",
    description:
      "Analyze the e-commerce price war in real time. Detect who's cutting prices, how often, what discount depth they're using, and how it impacts your positioning.",
    keywords: ["price competition", "e-commerce pricing strategy", "dynamic pricing"],
  },

  // ── Content Score ─────────────────────────────────────────────────────────
  "Puntuación de contenido": {
    icon: "📝",
    description:
      "Evalúa la calidad del contenido de tus fichas de producto: imágenes, descripciones, palabras clave y especificaciones. Un contenido optimizado mejora el SEO interno y convierte más.",
    keywords: ["content score", "optimización de fichas de producto", "SEO de producto"],
  },
  "Content Score": {
    icon: "📝",
    description:
      "Evaluate the content quality of your product listings: images, descriptions, keywords and specifications. Optimized content improves internal SEO and drives higher conversion.",
    keywords: ["content score", "product listing optimization", "product SEO"],
  },

  // ── Product Availability ──────────────────────────────────────────────────
  "Disponibilidad de productos": {
    icon: "✅",
    description:
      "Rastrea la disponibilidad exacta de cada SKU en cada tienda. Recibe alertas sobre productos agotados, fantasmas o con inventario crítico antes de que impacten tus ventas.",
    keywords: ["disponibilidad de producto", "out of stock", "gestión de inventario retail"],
  },
  "Product Availability": {
    icon: "✅",
    description:
      "Track the exact availability of every SKU at every store. Get alerts on out-of-stock, phantom or critically low inventory products before they hurt your sales.",
    keywords: ["product availability", "out of stock alerts", "retail inventory management"],
  },

  // ── Share of Innovation ───────────────────────────────────────────────────
  "Participación en la innovación": {
    icon: "🚀",
    description:
      "Mide cuántos SKUs nuevos lanza cada marca y cómo se distribuyen en los portales. Detecta innovaciones de la competencia el mismo día en que aparecen en el lineal digital.",
    keywords: ["share of innovation", "lanzamientos de producto", "nuevos SKUs e-commerce"],
  },
  "Share of Innovation": {
    icon: "🚀",
    description:
      "Measure how many new SKUs each brand is launching and how they are distributed across portals. Detect competitor innovations the same day they appear on the digital shelf.",
    keywords: ["share of innovation", "product launches", "new SKU tracking"],
  },

  // ── Market Price Index ────────────────────────────────────────────────────
  "Índice de precios de mercado": {
    icon: "📈",
    description:
      "Construye un índice de precios de mercado por categoría, retailer y período. Entiende si tu marca está por encima o por debajo del índice y toma decisiones de pricing informadas.",
    keywords: ["índice de precios", "market price index", "precio de referencia"],
  },
  "Market Price Index": {
    icon: "📈",
    description:
      "Build a market price index by category, retailer and time period. Understand whether your brand is above or below the index and make informed pricing decisions.",
    keywords: ["market price index", "category price index", "retail pricing benchmark"],
  },

  // ── Share of Shelf ────────────────────────────────────────────────────────
  "Participación en estanterías": {
    icon: "🏪",
    description:
      "Conoce qué porcentaje del lineal digital ocupa tu marca versus la competencia. El Share of Shelf digital es uno de los KPIs más críticos para el éxito en e-commerce.",
    keywords: ["share of shelf", "participación en estanterías", "digital shelf analytics"],
  },
  "Share of Shelf": {
    icon: "🏪",
    description:
      "Know what percentage of the digital shelf your brand occupies versus competitors. Digital Share of Shelf is one of the most critical KPIs for e-commerce success.",
    keywords: ["share of shelf", "digital shelf", "digital shelf analytics"],
  },

  // ── Share of Promotion ────────────────────────────────────────────────────
  "Participación en promociones": {
    icon: "🏷️",
    description:
      "Analiza qué marcas, categorías y retailers están promoviendo más, con qué intensidad y qué tipos de promoción usan. Optimiza tu estrategia promocional con datos reales.",
    keywords: ["share of promotion", "analítica promocional", "estrategia de promociones"],
  },
  "Share of Promotion": {
    icon: "🏷️",
    description:
      "Analyze which brands, categories and retailers are promoting most, with what intensity, and what types of promotions they use. Optimize your promotional strategy with real data.",
    keywords: ["share of promotion", "promotion analytics", "promotional strategy"],
  },

  // ── Region Price Index ────────────────────────────────────────────────────
  "Índice de precios regional": {
    icon: "🗺️",
    description:
      "Compara precios a nivel regional: por estado, ciudad o zona geográfica. Identifica disparidades de precio entre tiendas del mismo retailer y oportunidades de arbitraje.",
    keywords: ["índice de precios regional", "precios por región", "visibilidad regional"],
  },
  "Region Price Index": {
    icon: "🗺️",
    description:
      "Compare prices at a regional level: by state, city or geographic zone. Identify price disparities across stores of the same retailer and arbitrage opportunities.",
    keywords: ["regional price index", "price by region", "regional visibility"],
  },
};

function FeatureCard({ label, isTop }) {
  const [open, setOpen] = useState(false);
  const detail = ITEM_DETAILS[label] || {};

  return (
    <div
      className={`feature-card ${isTop ? "feature-card--top" : "feature-card--bottom"} ${open ? "feature-card--open" : ""}`}
      onClick={() => setOpen((v) => !v)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && setOpen((v) => !v)}
      aria-expanded={open}
    >
      <div className="feature-card__header">
        <span className="feature-card__icon">{detail.icon || "📌"}</span>
        <span className="feature-card__label">{label}</span>
        <span className="feature-card__chevron">{open ? "▲" : "▼"}</span>
      </div>
      {open && detail.description && (
        <div className="feature-card__body">
          <p>{detail.description}</p>
          {detail.keywords && (
            <ul className="feature-card__tags">
              {detail.keywords.map((kw) => (
                <li key={kw}>{kw}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Diagram(props) {
  const [ref, inView] = useInView({ threshold: 0.1 });

  const topItems = [props.t("seccion26_4"), props.t("seccion26_5")];
  const bottomItems = [
    props.t("seccion26_6"),
    props.t("seccion26_7"),
    props.t("seccion26_8"),
    props.t("seccion26_9"),
    props.t("seccion26_10"),
    props.t("seccion26_11"),
    props.t("seccion26_12"),
    props.t("seccion26_13"),
    props.t("seccion26_14"),
  ];

  return (
    <section
      ref={ref}
      className={`u-align-center u-clearfix u-section-26 ${inView ? "diagram-visible" : ""}`}
      id="Diagram"
      aria-label="Digital Shelf Analytics — Intelligence capabilities"
    >
      {/* SEO heading */}
      <h2 className="diagram-heading">
        <span className="u-text-palette-1-base">{props.t("seccion26_1")} </span>
        <span className="u-text-palette-2-base">{props.t("seccion26_2")} </span>
        <span className="u-text-palette-1-base">{props.t("seccion26_3")}</span>
      </h2>
      <p className="diagram-subheading">
        {props.t("seccion26_4")} &amp; {props.t("seccion26_5")} —{" "}
        <strong>Digital Shelf Analytics</strong> {" "}
		{props.t("diagram_sub")}
      </p>

      <div className="u-clearfix u-sheet u-sheet-1">
        {/* Top — two main pillars */}
        <div className="top-items-container">
          {topItems.map((item) => (
            <FeatureCard key={item} label={item} isTop />
          ))}
        </div>

        {/* Bottom — 9 specific capabilities */}
        <div className="bottom-items-grid">
          {bottomItems.map((item) => (
            <FeatureCard key={item} label={item} isTop={false} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default Diagram;
